import { TFunction } from 'i18next'
import React, { ReactElement, ReactNode } from 'react'
import { CheckoutError } from '../../model'
import { Trans } from '../../utils/convergence'

const RECOVERABLE_TERMINAL_ERROR_KEYS = [
  'amountTooHigh',
  'default',
  'maxActiveOrdersReached',
  'maxActiveOrdersReached_details',
  'overduePaymentEligible',
  'queryError',
] as const

export type RecoverableTerminalErrorKey = (typeof RECOVERABLE_TERMINAL_ERROR_KEYS)[number]

interface RecoverableTerminalErrorDescriptionParams {
  values: CheckoutError['extra']
  cartTotal: ReactElement
}

interface RecoverableTerminalErrorI18nConfig {
  heading: (t: TFunction) => string
  description: (t: TFunction) => string
  renderDescription: ({ values, cartTotal }: RecoverableTerminalErrorDescriptionParams) => ReactNode
}

const RECOVERABLE_TERMINAL_ERROR_I18N_CONFIGS: Record<RecoverableTerminalErrorKey, RecoverableTerminalErrorI18nConfig> = {
  amountTooHigh: {
    heading: (t) => t('recoverableTerminalError:amountTooHigh.heading'),
    description: (t) => t('recoverableTerminalError:amountTooHigh.description'),
    renderDescription: ({ values, cartTotal }) => (
      <Trans i18nKey='recoverableTerminalError:amountTooHigh.description' values={values} components={{ cartTotal }} />
    ),
  },
  default: {
    heading: (t) => t('recoverableTerminalError:default.heading'),
    description: (t) => t('recoverableTerminalError:default.description'),
    renderDescription: ({ values, cartTotal }) => <Trans i18nKey='recoverableTerminalError:default.description' values={values} components={{ cartTotal }} />,
  },
  maxActiveOrdersReached: {
    heading: (t) => t('recoverableTerminalError:maxActiveOrdersReached.heading'),
    description: (t) => t('recoverableTerminalError:maxActiveOrdersReached.description'),
    renderDescription: ({ values, cartTotal }) => (
      <Trans i18nKey='recoverableTerminalError:maxActiveOrdersReached.description' values={values} components={{ cartTotal }} />
    ),
  },
  maxActiveOrdersReached_details: {
    heading: (t) => t('recoverableTerminalError:maxActiveOrdersReached_details.heading'),
    description: (t) => t('recoverableTerminalError:maxActiveOrdersReached_details.description'),
    renderDescription: ({ values, cartTotal }) => (
      <Trans i18nKey='recoverableTerminalError:maxActiveOrdersReached_details.description' values={values} components={{ cartTotal }} />
    ),
  },
  overduePaymentEligible: {
    heading: (t) => t('recoverableTerminalError:overduePaymentEligible.heading'),
    description: (t) => t('recoverableTerminalError:overduePaymentEligible.description'),
    renderDescription: ({ values, cartTotal }) => (
      <Trans i18nKey='recoverableTerminalError:overduePaymentEligible.description' values={values} components={{ cartTotal }} />
    ),
  },
  queryError: {
    heading: (t) => t('recoverableTerminalError:queryError.heading'),
    description: (t) => t('recoverableTerminalError:queryError.description'),
    renderDescription: ({ values, cartTotal }) => (
      <Trans i18nKey='recoverableTerminalError:queryError.description' values={values} components={{ cartTotal }} />
    ),
  },
}

const isRecoverableTerminalErrorKey = (error: CheckoutError['error']): error is RecoverableTerminalErrorKey =>
  typeof error === 'string' && RECOVERABLE_TERMINAL_ERROR_KEYS.includes(error as RecoverableTerminalErrorKey)

export const selectRecoverableTerminalErrorKey = (error: CheckoutError['error']): RecoverableTerminalErrorKey => {
  if (isRecoverableTerminalErrorKey(error)) {
    return error
  }

  return 'default'
}

export const selectRecoverableTerminalErrorI18nConfig = (error: CheckoutError['error']): RecoverableTerminalErrorI18nConfig =>
  RECOVERABLE_TERMINAL_ERROR_I18N_CONFIGS[selectRecoverableTerminalErrorKey(error)]
